import { apiConstants } from "@data/constants/apiConstants"
import { authConstants } from "@data/constants/authConstants"
import { summerConstants } from "@data/constants/summerConstants"
import { authService } from "@data/services/authService"
import { subscriptionService } from "@data/services/subscriptionService"
import moment from "moment"
import {
    JUNE_TRANSITION,
    RESUME_OFFER_TYPE,
    RESUME_PREFERENCE,
    SUMMER_CHOICE_STATUS,
    SUMMER_COURSES_OFFER_TYPE,
    SUMMER_MONTH_CHOICE,
    SUMMER_RESUME_TYPE,
    deriveMonthSlotsFromStatus,
    deriveResumeConfigFromPreference,
    deriveSummerStatusFromChoices,
    getSummerMonthChoiceValue,
    getSummerMonthCoursesConfig,
    isConfirmedSummerStatus,
} from "@common/utils/summer2026"

// True quand un coursesConfig local ne porte aucune des clés dont
// `getSummerCoursesHoursPerWeek` a besoin pour produire un priceDescriptor
// non-null. Ça arrive après le rename `timeslotIds → slots_possible` côté
// FE : redux-persist garde des slots legacy qui ne match plus le rendu.
// Dans ce cas, mieux vaut accepter la version backend (single source de
// vérité après POST) plutôt que de laisser la card afficher l'empty state.
const isCoursesConfigUnrenderable = (config) => {
    if (!config) return true
    if (config.offerType === SUMMER_COURSES_OFFER_TYPE.SHARED) {
        return (
            !Array.isArray(config.slots_possible) ||
            config.slots_possible.length === 0
        )
    }
    if (config.offerType === SUMMER_COURSES_OFFER_TYPE.PRIVATE) {
        return !config.hoursPerWeek
    }
    // offerType absent → forcément non-renderable.
    return true
}

// Reducers run at module-load before `staticData` is hydrated from the
// backend, so we can't read `summer_config` here. Derive the calendar-year
// defaults inline; they're overridden as soon as the cockpit reads
// `getSummerConfigSafe(summerConfig)`. Keeping the shape (Y-07-01 / Y-08-31)
// works for any future year since July and August are always 31-day months.
const initialSummerYear = moment().year()
const initialPauseStartDate = `${initialSummerYear}-07-01`
const initialPauseEndDate = `${initialSummerYear}-08-31`

/**
 * Summer 2026 slice — local state that mirrors the `summer_*` fields the
 * backend exposes on the User resource (june_transition / summer_status /
 * resume_preference + the per-month / per-resume config selected in the
 * Summer Cockpit).
 *
 * Modèle (paramétrabilité cours d'été calquée sur create-subscription) :
 * - `july` / `august` : { choice, coursesConfig }
 *     - choice : "summer_courses" | "autonomy" | "pause"
 *     - coursesConfig : { hoursPerWeek, offerType, subjectIds } si
 *       choice === "summer_courses", sinon null
 * - `resumeSubjectIds` : matières à la reprise (override de l'abo courant)
 * - `status` : projection legacy recomputée pour banner / state line.
 */
const initialState = {
    status: SUMMER_CHOICE_STATUS.NOT_VISITED,
    decidedAt: null,
    pauseDates: {
        startDate: initialPauseStartDate,
        endDate: initialPauseEndDate,
    },
    stageBooking: null,
    juneTransition: JUNE_TRANSITION.NOT_SET,
    // Start date (ISO "YYYY-MM-DD") of an advanced June pause. null = default
    // continuation of the current formula until 30 June (nothing to persist).
    junePauseStartDate: null,
    // True once the parent has set OR cleared the June pause this session. A
    // plain null guard can't tell a deliberate back-out (null) from a
    // never-touched null, so this flag is what blocks a /me refresh from
    // silently re-seeding the saved date over a cleared draft. Reset on
    // logout / resetSummerChoice so a fresh login re-hydrates normally.
    junePauseTouched: false,
    resumePreference: RESUME_PREFERENCE.NOT_SET,
    july: null,
    august: null,
    resumeDate: null,
    resumeType: null,
    resumeFrequency: null,
    resumeCommitment: "monthly",
    // True once the parent has explicitly set the rentrée commitment this
    // session. `resumeCommitment` defaults to "monthly" (never empty), so a
    // plain value guard can't tell an untouched default from a deliberate
    // "monthly" pick — this flag is what stops a /me refresh from reverting an
    // in-progress monthly⇄yearly toggle. Reset on logout / switch / reset.
    resumeCommitmentTouched: false,
    resumeSubjectIds: null,
}

const buildMonthSlot = ({ previousSlot, choice, coursesConfig }) => {
    if (!choice) return null
    if (choice !== SUMMER_MONTH_CHOICE.SUMMER_COURSES) {
        return { choice, coursesConfig: null }
    }
    const previousConfig =
        previousSlot && typeof previousSlot !== "string"
            ? (previousSlot.coursesConfig ?? null)
            : null
    return {
        choice,
        coursesConfig: coursesConfig ?? previousConfig ?? null,
    }
}

// Seed the Summer slice from the UserResource payload (login / getUser /
// register / confirmSummerChoice all return the refreshed user). The
// backend only persists the 3-dimensions snapshot — granular per-month
// coursesConfig and rentrée subject overrides stay local to the cockpit.
//
// Hydration only runs when the backend exposes a *confirmed* cohort
// signal (`summer_summer_status` ∈ {chose_*, auto_paused}). For unsubmitted
// states we keep the local-first slice intact to avoid wiping the
// parent's in-progress edits across a /me refresh.
//
// Source of truth for fields not persisted backend-side:
// - july / august : coarse projection from `summer_summer_status` (both
//                   months take the same choice; parent can re-edit per
//                   month). If the local state already has a per-month
//                   slot, we preserve it (richer signal).
// - resumeType / resumeFrequency : derived from `summer_resume_preference`
//                   (Flex hour bands default to FLEX_SHARED — hero offer).
const hydrateFromUser = (state, user) => {
    if (!user) return state
    const backendStatus = user.summer_summer_status ?? user.summer_choice_status
    if (!isConfirmedSummerStatus(backendStatus)) return state

    const next = { ...state }
    next.status = backendStatus
    // `decided_at` is the backend confirm timestamp (set on every cockpit
    // POST). It's the signal that a saved trajectory exists, so the exit
    // guard treats hydrated choices as confirmed — not as an unsaved draft.
    next.decidedAt = user.summer_decided_at ?? state.decidedAt
    // June dimension — local-first: only seed from the backend while the parent
    // hasn't touched the June block this session. `junePauseTouched` covers both
    // a locally-set date AND a deliberate back-out (local null), so a /me refresh
    // can't re-seed the saved date over either edit. A logout / resetSummerChoice
    // clears the flag, so a fresh login re-hydrates the confirmed pause normally.
    if (!state.junePauseTouched) {
        if (user.summer_june_transition) {
            next.juneTransition = user.summer_june_transition
        }
        next.junePauseStartDate = user.summer_june_pause_start_date ?? null
    }
    if (user.summer_resume_preference) {
        next.resumePreference = user.summer_resume_preference
        const derivedResume = deriveResumeConfigFromPreference(
            user.summer_resume_preference
        )
        // `summer_resume_preference` doesn't distinguish shared vs individual
        // for an hour-band (hours_2h covers both), so the preference-only
        // derivation defaults to FLEX_SHARED. When the backend also exposes
        // `summer_resume_offer_type` (set explicitly by the cockpit confirm),
        // refine the type to match what the parent actually picked — keeps
        // the divergence check in `SummerCockpitScreen` from firing falsely
        // on a clean re-open of a Cours particuliers confirmation.
        const offerTypeOverride =
            user.summer_resume_offer_type === RESUME_OFFER_TYPE.INDIVIDUAL
                ? SUMMER_RESUME_TYPE.FLEX_INDIVIDUAL
                : user.summer_resume_offer_type === RESUME_OFFER_TYPE.SHARED
                  ? SUMMER_RESUME_TYPE.FLEX_SHARED
                  : user.summer_resume_offer_type === RESUME_OFFER_TYPE.AUTONOMY
                    ? SUMMER_RESUME_TYPE.AUTONOMY
                    : null
        const seededType = offerTypeOverride ?? derivedResume.type
        if (!state.resumeType && seededType) {
            next.resumeType = seededType
            next.resumeFrequency = derivedResume.frequency
        }
    }
    // Rentrée commitment — local-first like the other resume axes: only seed
    // from the backend while the parent hasn't toggled it this session, so a
    // /me refresh can't revert an in-progress monthly⇄yearly choice.
    if (!state.resumeCommitmentTouched && user.summer_resume_commitment) {
        next.resumeCommitment = user.summer_resume_commitment
    }
    // Per-month hydration — préfère les colonnes per-month si elles
    // existent (post-migration backend), sinon fallback sur le summer_status
    // legacy projeté identiquement sur juillet et août. Le backend renvoie
    // `summer_july_status` / `summer_august_status` (avec fallback sur
    // `summer_summer_status` côté Resource).
    const julyBackendStatus = user.summer_july_status ?? backendStatus
    const augustBackendStatus = user.summer_august_status ?? backendStatus
    const julySlot = deriveMonthSlotsFromStatus(julyBackendStatus)
    const augustSlot = deriveMonthSlotsFromStatus(augustBackendStatus)
    // Granular coursesConfig backend-side (depuis l'ajout des colonnes JSON
    // july_courses_config / august_courses_config). Si le backend renvoie
    // un blob non vide pour un mois en Cours d'été, on l'attache au slot
    // dérivé pour que le cockpit puisse repartir d'un état complet (offre,
    // créneaux, h-sem, matières) après un login frais.
    if (
        julySlot?.choice === SUMMER_MONTH_CHOICE.SUMMER_COURSES &&
        user.summer_july_courses_config
    ) {
        julySlot.coursesConfig = user.summer_july_courses_config
    }
    if (
        augustSlot?.choice === SUMMER_MONTH_CHOICE.SUMMER_COURSES &&
        user.summer_august_courses_config
    ) {
        augustSlot.coursesConfig = user.summer_august_courses_config
    }
    // july/august seedés UNIQUEMENT après reset Redux (logout/login,
    // fresh load, register) — i.e. quand le local state n'a pas encore
    // été touché par l'utilisateur. Jamais en cours de session : si
    // l'utilisateur a posé des choix mensuels, on les préserve face au
    // signal coarse projeté du backend (cf. local-first pattern).
    // A month whose leg has already started is LOCKED server-side
    // (`summer_*_locked`) — it can no longer be edited self-service, so a
    // persisted redux draft for it is stale by definition and the backend
    // status is the source of truth. Override the local slot for a locked month
    // (bypassing the local-first guard) so re-entering the cockpit shows what
    // will actually happen — not an unsaved edit the parent can no longer apply.
    // An unlocked month keeps the local-first guard (preserve in-progress edits).
    const julyLocked = !!user.summer_july_locked
    const augustLocked = !!user.summer_august_locked
    if (julySlot && (julyLocked || !state.july)) next.july = julySlot
    if (augustSlot && (augustLocked || !state.august)) next.august = augustSlot
    // Gap-fill : redux-persist peut conserver un slot legacy
    // `{choice: summer_courses, coursesConfig: null}` (créé avant la
    // migration backend, ou sur un re-confirm précédent sans rouvrir la
    // modale). Sur hydration suivante, on rapatrie la coursesConfig depuis
    // le backend tant que le choix local concorde — sans toucher au choice
    // (local-first prime). Sans ce branch, le slot reste null et la card
    // affiche "Configurez les créneaux et matières pour ce mois".
    if (
        !julyLocked &&
        state.july &&
        getSummerMonthChoiceValue(state.july) ===
            SUMMER_MONTH_CHOICE.SUMMER_COURSES &&
        isCoursesConfigUnrenderable(getSummerMonthCoursesConfig(state.july)) &&
        user.summer_july_courses_config
    ) {
        next.july = {
            choice: SUMMER_MONTH_CHOICE.SUMMER_COURSES,
            coursesConfig: user.summer_july_courses_config,
        }
    }
    if (
        !augustLocked &&
        state.august &&
        getSummerMonthChoiceValue(state.august) ===
            SUMMER_MONTH_CHOICE.SUMMER_COURSES &&
        isCoursesConfigUnrenderable(
            getSummerMonthCoursesConfig(state.august)
        ) &&
        user.summer_august_courses_config
    ) {
        next.august = {
            choice: SUMMER_MONTH_CHOICE.SUMMER_COURSES,
            coursesConfig: user.summer_august_courses_config,
        }
    }
    // Rentrée subject overrides : même règle local-first que `resumeType` —
    // on seed depuis le backend uniquement si le slice local n'a pas déjà
    // une valeur (préserve les édits en cours).
    if (!state.resumeSubjectIds && user.summer_resume_subject_ids) {
        next.resumeSubjectIds = user.summer_resume_subject_ids
    }
    if (!state.resumeDate && user.summer_resume_date) {
        next.resumeDate = user.summer_resume_date
    }
    return next
}

export function summer(state = initialState, action) {
    switch (action.type) {
        // Reset on logout AND on account switch: the summer slice is per-student
        // (one child's choices must never leak into a sibling's cockpit). When a
        // parent switches children, SWITCH_ACCOUNT clears the slice so the
        // following getUser re-hydrates fresh for the newly-selected student.
        case authConstants.LOGOUT_401:
        case authConstants.RESET_AUTH_STATE:
        case authConstants.SWITCH_ACCOUNT:
        case authService.logout.name + apiConstants._SUCCESS:
            return initialState

        // Hydrate from /me payload — login, getUser, register flows, and
        // confirmSummerChoice all return the refreshed UserResource. Lets
        // the cockpit reflect any cohort signal persisted backend-side
        // (e.g. admin override via the BO) the next time the parent opens
        // it, instead of starting from a blank initial state.
        case authService.login.name + apiConstants._SUCCESS:
        case authService.getUser.name + apiConstants._SUCCESS:
        case authService.updateUser.name + apiConstants._SUCCESS:
        case authService.registerV2.name + apiConstants._SUCCESS:
        case authService.registerTrialV2.name + apiConstants._SUCCESS:
        case authService.registerSpecialV2.name + apiConstants._SUCCESS:
        case authService.registerWorkshopV2.name + apiConstants._SUCCESS:
        case authService.registerAndGetSanctumToken.name +
            apiConstants._SUCCESS:
        case authService.getSanctumToken.name + apiConstants._SUCCESS:
        case authService.verifyLoginCode.name + apiConstants._SUCCESS:
        case subscriptionService.confirmSummerChoice.name +
            apiConstants._SUCCESS:
            return hydrateFromUser(state, action.data?.user ?? action.data)

        case summerConstants.MARK_SUMMER_COCKPIT_VIEWED: {
            if (state.status !== SUMMER_CHOICE_STATUS.NOT_VISITED) return state
            return { ...state, status: SUMMER_CHOICE_STATUS.VIEWED_NO_CHOICE }
        }

        case summerConstants.SET_SUMMER_CHOICE:
            return {
                ...state,
                status: action.status,
                decidedAt: action.decidedAt,
            }

        case summerConstants.SET_SUMMER_PAUSE_DATES:
            return {
                ...state,
                pauseDates: action.pauseDates || state.pauseDates,
            }

        case summerConstants.SET_SUMMER_STAGE_BOOKING:
            return { ...state, stageBooking: action.stageBooking }

        case summerConstants.SET_JUNE_PAUSE: {
            // A start date advances a June pause; null restores the default
            // continuation (nothing persisted backend-side). Either way the
            // parent has touched the block — flag it so hydration won't re-seed
            // the saved date over this draft until the next confirm / reset.
            if (action.junePauseStartDate) {
                return {
                    ...state,
                    juneTransition: JUNE_TRANSITION.PAUSE_EARLY,
                    junePauseStartDate: action.junePauseStartDate,
                    junePauseTouched: true,
                }
            }
            return {
                ...state,
                juneTransition: JUNE_TRANSITION.NOT_SET,
                junePauseStartDate: null,
                junePauseTouched: true,
            }
        }

        case summerConstants.SET_RESUME_PREFERENCE:
            return { ...state, resumePreference: action.resumePreference }

        case summerConstants.SET_SUMMER_MONTH_CHOICE: {
            const nextSlot = buildMonthSlot({
                previousSlot: state[action.month],
                choice: action.choice,
                coursesConfig: action.coursesConfig,
            })
            const next = { ...state, [action.month]: nextSlot }
            next.status = deriveSummerStatusFromChoices(next.july, next.august)
            return next
        }

        case summerConstants.SET_SUMMER_MONTH_COURSES_CONFIG: {
            const previous = state[action.month]
            const previousChoice =
                previous && typeof previous !== "string"
                    ? previous.choice
                    : previous
            if (previousChoice !== SUMMER_MONTH_CHOICE.SUMMER_COURSES)
                return state
            return {
                ...state,
                [action.month]: {
                    choice: SUMMER_MONTH_CHOICE.SUMMER_COURSES,
                    coursesConfig: action.coursesConfig ?? null,
                },
            }
        }

        case summerConstants.SET_SUMMER_RESUME_DATE:
            return { ...state, resumeDate: action.resumeDate }

        case summerConstants.SET_SUMMER_RESUME_OFFER:
            return {
                ...state,
                resumeType: action.resumeType ?? state.resumeType,
                resumeFrequency:
                    action.resumeFrequency !== undefined
                        ? action.resumeFrequency
                        : state.resumeFrequency,
                resumeCommitment:
                    action.resumeCommitment ?? state.resumeCommitment,
                resumeCommitmentTouched:
                    action.resumeCommitment != null
                        ? true
                        : state.resumeCommitmentTouched,
            }

        case summerConstants.SET_SUMMER_RESUME_CONFIG: {
            const patch = {}
            if (action.resumeType !== undefined)
                patch.resumeType = action.resumeType
            if (action.resumeFrequency !== undefined)
                patch.resumeFrequency = action.resumeFrequency
            if (action.resumeCommitment !== undefined) {
                patch.resumeCommitment = action.resumeCommitment
                patch.resumeCommitmentTouched = true
            }
            if (action.resumeDate !== undefined)
                patch.resumeDate = action.resumeDate
            if (action.resumeSubjectIds !== undefined)
                patch.resumeSubjectIds = action.resumeSubjectIds
            return { ...state, ...patch }
        }

        case summerConstants.CONFIRM_SUMMER_CHOICES:
            return { ...state, decidedAt: new Date().toISOString() }

        case summerConstants.RESET_SUMMER_CHOICE:
            return initialState

        default:
            return state
    }
}
